import React from 'react';
import {useCheckboxGroup, useCheckboxGroupItem} from '@react-aria/checkbox';
import {useFocusRing} from '@react-aria/focus';
import {useCheckboxGroupState} from '@react-stately/checkbox';
import classNames from 'classnames';

import {TypographySize, useTypography} from '../../../hooks/typography';
import {CheckboxView} from '../../../atoms/checkbox';

import type {Item, Props} from './types';
import styles from './styles.module.css';

const Checkbox: React.VoidFunctionComponent<Item> = (props) => {
    const {state, label, invalid, checkboxSize, inline, spaceful, ...rest} = props;
    const ref = React.useRef(null);
    const {inputProps} = useCheckboxGroupItem(rest, state, ref);
    const {isFocusVisible, focusProps} = useFocusRing();

    const isDisabled = state.isDisabled || props.isDisabled;
    const isSelected = state.isSelected(props.value);

    return (
        <CheckboxView
            checked={isSelected}
            label={label}
            checkboxSize={checkboxSize}
            disabled={isDisabled}
            focused={isFocusVisible}
            invalid={invalid}
            inline={inline}
            spaceful={spaceful}
            {...inputProps}
            {...focusProps}
        />
    );
};

export const MultiCheckbox: React.VoidFunctionComponent<Props> = (props) => {
    const {label, options, inline, invalid, checkboxSize, spaceful} = props;
    const state = useCheckboxGroupState(props);
    const {groupProps, labelProps} = useCheckboxGroup(props, state);
    const typographyCls = useTypography({
        size: TypographySize.BodyL,
    });

    return (
        <div {...groupProps}>
            {label ? (
                <span {...labelProps} className={classNames(styles.mainLabel, typographyCls, invalid && styles.invalid)}>
                    {label}
                </span>
            ) : null}
            <div className={classNames(styles.checkboxesWrapper, inline && styles.inline)}>
                {options.map((option) => (
                    <Checkbox
                        key={option.text}
                        label={option.text}
                        invalid={invalid}
                        value={option.value as string}
                        state={state}
                        inline={inline}
                        spaceful={spaceful}
                        checkboxSize={checkboxSize}
                    />
                ))}
            </div>
        </div>
    );
};
